"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.joinTransformOp = exports.getJoinTransformOpOutputDataTape = exports.sanitizeTransformOp = exports.getSanitizeTransformOpOutputDataTape = exports.rowDataToCsv = exports.untypedRowDataToCsv = exports.getSanitizedConfig = exports.getSanitizedDataTapeRowData = exports.getSanitizedCsvData = exports.getSanitizedRowData = exports.getSanitizedDataTapeField = exports.getHeaders = exports.validateHeaders = exports.getSanitizedFieldName = exports.handleUnexpectedSanitizeErrors = exports.getSantizedTapeErrorSummary = exports.getCompactedDataTapeErrors = void 0;
const csvtojson_1 = __importDefault(require("csvtojson"));
const lodash_1 = __importDefault(require("lodash"));
const remeda_1 = require("remeda");
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const typeguards_1 = require("./typeguards");
// excel/csv have a header row and are 1 based arrays, so adjust for our 0 based rowData
const STANDARD_ROW_INDEX_OFFSET = 2;
const getFieldValidationErrorMessage = (invalidFields) => {
    return `Validation Failed: ${invalidFields.length} invalid fields: [${invalidFields.map((field) => field.name).join(', ')}]`;
};
const ERROR_COUNT_THRESHOLD = 20;
const getCompactedDataTapeErrors = (errors, threshold = ERROR_COUNT_THRESHOLD) => {
    let fieldValidationErrors = [];
    const columnValidationErrors = [];
    const otherErrors = [];
    errors.forEach((error) => {
        switch (error.type) {
            case 'fieldValidation':
                fieldValidationErrors.push(error);
                break;
            case 'columnValidation':
                columnValidationErrors.push(error);
                break;
            default:
                otherErrors.push(error);
                break;
        }
    });
    // get all invalid field names from FieldValidationErrors and unique them by name only
    const invalidFields = (0, remeda_1.pipe)(fieldValidationErrors, (0, remeda_1.map)((err) => err.invalidFields), (0, remeda_1.flatten)(), (0, remeda_1.uniqBy)((field) => field.name));
    // for each invalid field, check how many times it occurs in all FieldValidationErrors
    // if that number is greater than threshold or ERROR_COUNT_THRESHOLD, then add a ColumnValidationError
    // and remove that invalidField from all FieldValidationErrors
    // if there are no more invalidFields in the error, drop the error completely
    lodash_1.default.forEach(invalidFields, (field) => {
        const errContainsField = lodash_1.default.filter(fieldValidationErrors, (err) => lodash_1.default.some(err.invalidFields, (checkField) => checkField.name === field.name));
        if (errContainsField.length > threshold) {
            columnValidationErrors.push({
                type: 'columnValidation',
                // value is always empty since this is across multiple errors
                field: Object.assign(Object.assign({}, field), { value: '' }),
                message: `Invalid field for ${errContainsField.length} rows: ${field.name} is invalid, expected type ${field.expectedType}${field.expectedFormat ? ` (${field.expectedFormat})` : ''}`,
            });
            const fieldValidationErrorsCopy = [...fieldValidationErrors];
            fieldValidationErrors = [];
            lodash_1.default.forEach(fieldValidationErrorsCopy, (err) => {
                const updatedInvalidFields = lodash_1.default.filter(err.invalidFields, (errInvalidField) => errInvalidField.name !== field.name);
                if (updatedInvalidFields.length) {
                    const newError = Object.assign(Object.assign({}, err), { invalidFields: updatedInvalidFields, message: getFieldValidationErrorMessage(updatedInvalidFields) });
                    fieldValidationErrors.push(newError);
                }
            });
        }
    });
    return [...fieldValidationErrors, ...columnValidationErrors, ...otherErrors];
};
exports.getCompactedDataTapeErrors = getCompactedDataTapeErrors;
const getSantizedTapeErrorSummary = (errors) => {
    const metadataValidationErrors = lodash_1.default.filter(errors, typeguards_1.isMetadataError);
    const fieldValidationErrors = lodash_1.default.filter(errors, typeguards_1.isFieldValidationError);
    const invalidFields = lodash_1.default.chain(fieldValidationErrors)
        .map((e) => e.invalidFields.map((field) => field.name))
        .flatten()
        .uniq()
        .value();
    const messages = [];
    if (metadataValidationErrors.length)
        messages.push(`Data tape has ${metadataValidationErrors.length} errors that prevent file parsing.`);
    if (fieldValidationErrors.length)
        messages.push(`Data tape has invalid fields (${invalidFields.join(', ')}) across ${fieldValidationErrors.length} rows.`);
    return messages.join('\n');
};
exports.getSantizedTapeErrorSummary = getSantizedTapeErrorSummary;
const handleUnexpectedSanitizeErrors = (errors, message) => {
    if (common_1.typeguards.isErrorType(errors) ||
        !lodash_1.default.every(errors, typeguards_1.isTransformError)) {
        const unexpectedErrors = lodash_1.default.isArray(errors)
            ? lodash_1.default.filter(errors, (e) => !(0, typeguards_1.isTransformError)(e))
            : [errors];
        const errStack = lodash_1.default.map(unexpectedErrors, (e) => e.stack);
        const errMessage = `["${lodash_1.default.map(unexpectedErrors, (e) => e.message).join('", "')}"]`;
        throw new common_1.error.FinleyError(`${message}: ${errMessage}`, {
            metadata: {
                logMeta: {
                    errStack,
                },
            },
        });
    }
};
exports.handleUnexpectedSanitizeErrors = handleUnexpectedSanitizeErrors;
const getSanitizedFieldName = (fieldName, sanitizeFieldNames) => (sanitizeFieldNames ? lodash_1.default.snakeCase(fieldName) : fieldName);
exports.getSanitizedFieldName = getSanitizedFieldName;
const validateHeaders = ({ headers, externalIdField, }) => {
    const errors = [];
    const uniqueHeaders = lodash_1.default.uniq(headers);
    if (uniqueHeaders.length !== headers.length) {
        const duplicateHeaders = (0, remeda_1.pipe)(headers, (0, remeda_1.groupBy)((header) => header), remeda_1.values, (0, remeda_1.filter)((dupeHeaders) => dupeHeaders.length > 1), (0, remeda_1.flatten)(), (0, remeda_1.uniq)());
        errors.push({
            type: 'metadataValidation',
            message: `Duplicate Headers: ["${duplicateHeaders.join('", "')}"]`,
        });
    }
    if (lodash_1.default.isString(externalIdField) && !lodash_1.default.includes(headers, externalIdField)) {
        errors.push({
            type: 'metadataValidation',
            message: `External Id Field does not exist: ${externalIdField}`,
        });
    }
    return errors;
};
exports.validateHeaders = validateHeaders;
/**
 * Reads sanitized headers from first line of csv file.
 *
 * If `sanitizeFieldNames` is true, headers will be
 * sanitized to be snake_cased. Otherwise,
 * headers will read from the CSV as-is.
 * Throws error if externalIdField does not exist in csv.
 */
const getHeaders = ({ csvString, delimiter, externalIdField, sanitizeFieldNames, }) => __awaiter(void 0, void 0, void 0, function* () {
    let headers = [];
    yield (0, csvtojson_1.default)({ delimiter })
        .fromString(csvString.split('\n')[0])
        .on('header', (rawheaders) => {
        headers = rawheaders.map((rawHeader) => (0, exports.getSanitizedFieldName)(rawHeader, sanitizeFieldNames));
    });
    const errors = (0, exports.validateHeaders)({ headers, externalIdField });
    if (errors.length) {
        throw errors;
    }
    return headers;
});
exports.getHeaders = getHeaders;
const getExpectedFormat = (baseFieldConfig) => {
    switch (baseFieldConfig.type) {
        case 'date':
        case 'optionalDate':
            return baseFieldConfig.externalDateFormat;
        // dateTimes resolve to iso dates
        case 'dateTime':
        case 'optionalDateTime':
            return baseFieldConfig.externalDateTimeFormat;
        default:
            return undefined;
    }
};
/**
 * Method to sanitize a single field, will throw an InvalidField if it errors
 * @param baseFieldConfig
 * @param value
 * @returns
 */
const getSanitizedDataTapeField = (baseFieldConfig, value, preSanitized = false) => {
    const isRequiredField = (0, typeguards_1.isRequiredBaseFieldConfig)(baseFieldConfig);
    if (!isRequiredField) {
        // for optional fields, user can (optionally) specify list of externalNullValues
        // that they want to be treated as null
        const externalNullValues = baseFieldConfig.externalNullValues || [];
        if (value === undefined || externalNullValues.includes(value)) {
            return null;
        }
    }
    // csvtojson can return an empty string or an object without a column defined, so handle both cases
    // this handles all required fields that are missing and all optional that are missing
    if (lodash_1.default.isNil(value) ||
        common_1.stringConversion.isEmptyString(value) ||
        // @dani we should be able to remove this str null conditional after csv conversion library callsite moves are done
        value === 'null') {
        if ((0, typeguards_1.isRequiredBaseFieldConfig)(baseFieldConfig) &&
            baseFieldConfig.defaultTo !== undefined) {
            (0, common_1.finleyAssert)(!common_1.stringConversion.isEmptyString(baseFieldConfig.defaultTo) &&
                baseFieldConfig.defaultTo !== 'null', 'setting empty string or "null" as defaultTo will cause infinite loop');
            return (0, exports.getSanitizedDataTapeField)(baseFieldConfig, baseFieldConfig.defaultTo);
        }
        if (isRequiredField) {
            // eslint-disable-next-line no-throw-literal
            throw {
                name: baseFieldConfig.field,
                value: undefined,
                expectedType: baseFieldConfig.type,
                expectedFormat: getExpectedFormat(baseFieldConfig),
            };
        }
        return null;
    }
    try {
        if ((0, typeguards_1.isBooleanBaseFieldConfig)(baseFieldConfig)) {
            return common_1.stringConversion.convertStringBoolean(value, preSanitized ? 'true' : baseFieldConfig.externalBooleanTrue);
        }
        if ((0, typeguards_1.isCurrencyBaseFieldConfig)(baseFieldConfig)) {
            return common_1.stringConversion.convertStrictCurrencyFactory(baseFieldConfig.currencyCode)(value);
        }
        if ((0, typeguards_1.isDateBaseFieldConfig)(baseFieldConfig)) {
            if (preSanitized) {
                if (!common_1.typeguards.isFullDateStr(value)) {
                    // eslint-disable-next-line no-throw-literal
                    throw {
                        name: baseFieldConfig.field,
                        value,
                        expectedType: 'Sanitized FullDateStr',
                        expectedFormat: 'yyyy-LL-dd',
                    };
                }
                return value;
            }
            return common_1.date.strToFullDateStr(value, baseFieldConfig.externalDateFormat);
        }
        if ((0, typeguards_1.isDateTimeBaseFieldConfig)(baseFieldConfig)) {
            if (preSanitized) {
                if (!common_1.typeguards.isFullDateTimeStr(value)) {
                    // eslint-disable-next-line no-throw-literal
                    throw {
                        name: baseFieldConfig.field,
                        value,
                        expectedType: 'Sanitized FullDateTimeStr',
                        expectedFormat: `yyyy-MM-dd'T'HH:mm:ss.SSS'Z'`,
                    };
                }
                return value;
            }
            const dateTime = common_1.date.strToDateTime(value, baseFieldConfig.externalDateTimeFormat, baseFieldConfig.defaultTz);
            return common_1.date.dateTimetoFullDateTimeStr(dateTime);
        }
        if ((0, typeguards_1.isFloatBaseFieldConfig)(baseFieldConfig)) {
            return common_1.stringConversion.convertStrictNumber(value);
        }
        if ((0, typeguards_1.isIntegerBaseFieldConfig)(baseFieldConfig)) {
            return common_1.stringConversion.convertStrictInteger(value);
        }
        if ((0, typeguards_1.isPercentageBaseFieldConfig)(baseFieldConfig)) {
            // don't double sanitize a percent, return the number instead
            if (preSanitized) {
                return common_1.stringConversion.convertStrictNumber(value);
            }
            return common_1.stringConversion.convertStrictPercentageToFraction(value);
        }
        if ((0, typeguards_1.isStringBaseFieldConfig)(baseFieldConfig)) {
            return common_1.stringConversion.convertStrictNonEmptyString(lodash_1.default.trim(value));
        }
    }
    catch (err) {
        if ((0, typeguards_1.isInvalidField)(err)) {
            throw err;
        }
        // eslint-disable-next-line no-throw-literal
        throw {
            name: baseFieldConfig.field,
            value,
            expectedType: baseFieldConfig.type,
            expectedFormat: getExpectedFormat(baseFieldConfig),
        };
    }
    return value;
};
exports.getSanitizedDataTapeField = getSanitizedDataTapeField;
/**
 * Method to sanitize a row of CSV data
 * Throws a LoanTapeValidationError if there are any errors in the data
 * @param row
 * @param rowIndex generally will be index + STANDARD_ROW_INDEX_OFFSET to account for header row
 *   and 1 based array format of csv
 * @param baseFieldConfigMap
 * @param externalReferenceIdField
 * @returns
 */
const getSanitizedRowData = ({ row, rowIndex, baseFieldConfigMap, externalReferenceIdField, preSanitized, }) => {
    const invalidFields = [];
    const fields = lodash_1.default.keys(baseFieldConfigMap);
    const data = lodash_1.default.reduce(fields, (acc, field) => {
        let value;
        try {
            value = (0, exports.getSanitizedDataTapeField)(baseFieldConfigMap[field], row[field], preSanitized);
        }
        catch (err) {
            (0, common_1.finleyAssert)((0, typeguards_1.isInvalidField)(err), new common_1.error.FinleyError(`Unexpected row sanitization error: ${err.message}`, {
                metadata: {
                    logMeta: {
                        value,
                        errStack: err.stack,
                    },
                },
            }));
            invalidFields.push(err);
        }
        // eslint-disable-next-line no-param-reassign
        acc[field] = value;
        return acc;
    }, {});
    if (invalidFields.length) {
        // eslint-disable-next-line no-throw-literal
        throw {
            type: 'fieldValidation',
            row: rowIndex,
            externalReceivableId: externalReferenceIdField
                ? row[externalReferenceIdField]
                : 'unspecified',
            invalidFields,
            message: getFieldValidationErrorMessage(invalidFields),
        };
    }
    return data;
};
exports.getSanitizedRowData = getSanitizedRowData;
/**
 * Method to sanitize an array of CSV data
 * @param csvArray
 * @param baseFieldConfig
 * @param externalReferenceIdField
 * @returns
 */
const getSanitizedCsvData = ({ csvArray, baseFieldConfig, externalReferenceIdField, preSanitized = false, }) => {
    const errors = [];
    const baseFieldConfigMap = lodash_1.default.keyBy(baseFieldConfig, 'field');
    const data = lodash_1.default.map(csvArray, (row, rowIndex) => {
        try {
            const rowData = (0, exports.getSanitizedRowData)({
                row,
                // excel/csv have a header row and are 1 based arrays, so adjust for our 0 based rowData
                rowIndex: rowIndex + STANDARD_ROW_INDEX_OFFSET,
                baseFieldConfigMap,
                externalReferenceIdField,
                preSanitized,
            });
            rowData.finley_sanitized = true;
            return rowData;
        }
        catch (err) {
            (0, common_1.finleyAssert)((0, typeguards_1.isTransformError)(err), `Unexpected tape sanitization error: ${err.message}`);
            errors.push(err);
            return null;
        }
    });
    // if there are any errors, then throw them all and no data since it is invalid
    if (errors.length) {
        throw errors;
    }
    // data cannot have any null elements at this point
    // since we throw in those cases. but to keep TS happy filter out nulls.
    return data.filter(common_1.typeguards.isNotNullOrUndefined);
};
exports.getSanitizedCsvData = getSanitizedCsvData;
const getSanitizedDataTapeRowData = (row, rowIndex, baseFieldConfigMap) => {
    const invalidFields = [];
    const fields = lodash_1.default.keys(baseFieldConfigMap);
    const data = lodash_1.default.reduce(fields, (acc, field) => {
        let value;
        try {
            value = (0, exports.getSanitizedDataTapeField)(baseFieldConfigMap[field], row[field]);
        }
        catch (err) {
            (0, common_1.finleyAssert)((0, typeguards_1.isInvalidField)(err), `Unexpected data tape row sanitization error - getSanitizedDataTapeRowData: ${err.message}`);
            invalidFields.push(err);
        }
        // eslint-disable-next-line no-param-reassign
        acc[field] = value;
        return acc;
    }, {});
    if (invalidFields.length) {
        // eslint-disable-next-line no-throw-literal
        throw {
            type: 'fieldValidation',
            row: rowIndex,
            invalidFields,
            message: getFieldValidationErrorMessage(invalidFields),
        };
    }
    return data;
};
exports.getSanitizedDataTapeRowData = getSanitizedDataTapeRowData;
/**
 * Returns the type that a field will have after sanitization.
 * It also returns new name (if sanitizeFieldNames=true)
 */
const getSanitizedConfig = ({ configBeforeSanitize, sanitizeFieldNames, }) => {
    const bf = configBeforeSanitize;
    const field = (0, exports.getSanitizedFieldName)(configBeforeSanitize.field, sanitizeFieldNames);
    switch (bf.type) {
        // all these types resolve to float number
        case 'currency':
        case 'float':
        case 'percentage':
            return {
                field,
                type: 'float',
            };
        case 'optionalCurrency':
        case 'optionalFloat':
        case 'optionalPercentage':
            return {
                field,
                type: 'optionalFloat',
            };
        // booleans resolve to true as external
        case 'boolean':
        case 'optionalBoolean':
            return {
                field,
                type: bf.type,
                externalBooleanTrue: 'true',
            };
        // all these types resolve to the same type
        case 'integer':
        case 'string':
        case 'optionalInteger':
        case 'optionalString':
            return {
                field,
                type: bf.type,
            };
        // dates resolve to fulldatestr
        case 'date':
        case 'optionalDate':
            return {
                field,
                type: bf.type,
                externalDateFormat: 'yyyy-LL-dd',
            };
        // dateTimes resolve to iso dates
        case 'dateTime':
        case 'optionalDateTime':
            return {
                field,
                type: bf.type,
                externalDateTimeFormat: `yyyy-MM-dd'T'HH:mm:ss.SSS'Z'`,
                defaultTz: finley_types_1.Util.DateTimeZones.UTC,
            };
        default:
            return (0, common_1.finleyAssertUnreachable)(bf);
    }
};
exports.getSanitizedConfig = getSanitizedConfig;
const untypedRowDataToCsv = (data, fallbackHeaders) => {
    if (data.length) {
        return common_1.csvConversion.csvArrayToFinleyBuffer({
            csvArray: data,
            delimiter: ',',
            quotes: true,
            newline: '\n',
        });
    }
    // If `data` is an empty array, result will be '[]' which is not valid CSV. Since we get header
    // information from the field names of the objects, we need a fake object.
    const fakeRow = lodash_1.default.fromPairs(fallbackHeaders.map((x) => [x, x]));
    const fakeCsv = common_1.csvConversion.arrayToCsvString({
        csvArray: [fakeRow],
        header: true,
        delimiter: ',',
        quotes: true,
        newline: '\n',
    });
    // Keep only the header row, discarding the fake entry.
    const outputBuffer = fakeCsv.split('\n')[0];
    return common_1.finleyBuffer.fromString(outputBuffer);
};
exports.untypedRowDataToCsv = untypedRowDataToCsv;
const rowDataToCsv = (data, tapeDefinition) => (0, exports.untypedRowDataToCsv)(data, tapeDefinition.baseFields.map(({ field }) => field));
exports.rowDataToCsv = rowDataToCsv;
const getSanitizeTransformOpOutputDataTape = (transformation) => {
    const baseFields = transformation.srcDataTape.baseFields.map((config) => (0, exports.getSanitizedConfig)({
        configBeforeSanitize: config,
        sanitizeFieldNames: transformation.sanitizeFieldNames,
    }));
    return {
        baseFields,
        delimiter: ',',
        sanitizedFieldNames: transformation.sanitizeFieldNames,
    };
};
exports.getSanitizeTransformOpOutputDataTape = getSanitizeTransformOpOutputDataTape;
const sanitizeTransformOp = (input) => __awaiter(void 0, void 0, void 0, function* () {
    const { srcDataTape, sanitizeFieldNames } = input.transformation;
    // In `SanitizeTransformation`, base field names are configured with their pre-sanitize naming,
    // i.e. columns are named exactly as customers send to us. getHeaders() above will standardize
    // these namings, so output data uses standardized names too, thus sanitizing config here as well
    const baseFieldConfigMap = srcDataTape.baseFields.reduce((configMap, baseField) => {
        const sanitizedField = (0, exports.getSanitizedFieldName)(baseField.field, sanitizeFieldNames);
        // eslint-disable-next-line no-param-reassign
        configMap[sanitizedField] = Object.assign(Object.assign({}, baseField), { field: sanitizedField });
        return configMap;
    }, {});
    const { csvArray } = common_1.csvConversion.finleyBufferToCsvArray({
        buffer: input.srcDataTapeBuffer,
        delimiter: srcDataTape.delimiter,
        headerRenameFunction: (rawHeader) => (0, exports.getSanitizedFieldName)(rawHeader, sanitizeFieldNames),
    });
    // TODO: @sachinr4 throw errors on CSV conversion steps (this will be called during loan tape transformation when using sanitizeDataTape function)
    const outputData = [];
    const outputErrors = [];
    let rowIdx = STANDARD_ROW_INDEX_OFFSET;
    for (const csvRow of csvArray) {
        try {
            const sanitizedRowData = (0, exports.getSanitizedDataTapeRowData)(csvRow, rowIdx, baseFieldConfigMap);
            outputData.push(sanitizedRowData);
        }
        catch (error) {
            // No more assertion required here:
            // rowDataToCsv (called prior to return) already checks for errors that are not transform errors
            outputErrors.push(error);
        }
        rowIdx += 1;
    }
    if (outputErrors.length) {
        (0, exports.handleUnexpectedSanitizeErrors)(outputErrors, 'Unexpected sanitization transform error - sanitizeTransformOp');
        return {
            isValid: false,
            errors: outputErrors,
        };
    }
    const outputDataTape = (0, exports.getSanitizeTransformOpOutputDataTape)(input.transformation);
    const outputBuffer = (0, exports.rowDataToCsv)(outputData, outputDataTape);
    return {
        isValid: true,
        data: { outputDataTape, outputBuffer },
        supplementals: [],
    };
});
exports.sanitizeTransformOp = sanitizeTransformOp;
const checkJoinTransformOp = (transformation) => {
    const errors = [];
    const srcJoinOnBaseField = lodash_1.default.find(transformation.srcDataTape.baseFields, (bf) => bf.field === transformation.joinOnField);
    // check that joinOnField exists in srcDataTape basefields
    if (srcJoinOnBaseField === undefined) {
        errors.push({
            type: 'metadataValidation',
            message: `Source DataTape baseFields is missing joinOnField - checkJoinTransformOp: ${transformation.joinOnField}`,
        });
    }
    // check that srcDataTape joinOnField basefield is required
    if (srcJoinOnBaseField !== undefined &&
        !(0, typeguards_1.isRequiredBaseFieldConfig)(srcJoinOnBaseField)) {
        errors.push({
            type: 'metadataValidation',
            message: `Source DataTape joinOnField baseField must be a required field - checkJoinTransformOp: ${transformation.joinOnField}`,
        });
    }
    const joinJoinOnBaseField = lodash_1.default.find(transformation.joinDataTape.baseFields, (bf) => bf.field === transformation.joinOnField);
    // check that joinOnField exists in joinDataTape basefields
    if (joinJoinOnBaseField === undefined) {
        errors.push({
            type: 'metadataValidation',
            message: `Join DataTape baseFields is missing joinOnField - checkJoinTransformOp: ${transformation.joinOnField}`,
        });
    }
    // check that srcDataTape joinOnField basefield is required
    if (joinJoinOnBaseField !== undefined &&
        !(0, typeguards_1.isRequiredBaseFieldConfig)(joinJoinOnBaseField)) {
        errors.push({
            type: 'metadataValidation',
            message: `Join DataTape joinOnField baseField must be a required field - checkJoinTransformOp: ${transformation.joinOnField}`,
        });
    }
    // check that srcDataTape and joinDataTape joinOnField basefield are the same type
    if (srcJoinOnBaseField !== undefined &&
        joinJoinOnBaseField !== undefined &&
        (srcJoinOnBaseField === null || srcJoinOnBaseField === void 0 ? void 0 : srcJoinOnBaseField.type) !== joinJoinOnBaseField.type) {
        errors.push({
            type: 'metadataValidation',
            message: `Source DataTape and Join DataTape joinOnField baseFields must be the same type - checkJoinTransformOp: ${transformation.joinOnField} ${srcJoinOnBaseField.type} ${joinJoinOnBaseField.type}`,
        });
    }
    // check that all joinFields exist in joinData basefields
    const missingJoinFields = lodash_1.default.filter(transformation.joinFields, (jf) => !lodash_1.default.some(transformation.joinDataTape.baseFields, (bf) => bf.field === jf.field));
    if (missingJoinFields.length !== 0) {
        errors.push({
            type: 'metadataValidation',
            message: `Join DataTape missing joinFields - checkJoinTransformOp: ${missingJoinFields
                .map((jf) => jf.field)
                .join(',')}`,
        });
    }
    const srcFields = transformation.srcDataTape.baseFields.map((bf) => bf.field);
    // remove joinOnField from srcFields
    lodash_1.default.pull(srcFields, transformation.joinOnField);
    // check that all srcFields and joinFields are unique
    const dupeFields = lodash_1.default.chain([
        ...srcFields,
        ...transformation.joinFields.map((jf) => jf.field),
    ])
        .groupBy()
        .pickBy((f) => f.length > 1)
        .keys()
        .value();
    if (dupeFields.length !== 0) {
        errors.push({
            type: 'metadataValidation',
            message: `Duplicate sourceFields and joinFields - checkJoinTransformOp: ${dupeFields.join(',')}`,
        });
    }
    if (!transformation.errorOnMissingJoinRow) {
        // check that all joinFields conform to join datatape basefield configurations
        const joinBaseFieldsMap = lodash_1.default.keyBy(transformation.joinDataTape.baseFields, 'field');
        lodash_1.default.forEach(transformation.joinFields, (jf) => {
            const baseField = joinBaseFieldsMap[jf.field];
            // check if the baseField is required, if so, the joinField must have a default to
            if ((0, typeguards_1.isRequiredBaseFieldConfig)(baseField) && jf.defaultTo === undefined) {
                errors.push({
                    type: 'metadataValidation',
                    message: `Join BaseField is required, must have defaultTo value for joinField - checkJoinTransformOp: ${jf.field}`,
                });
            }
            // if there is a defaultTo value, it must conform to the baseField configuration
            if (jf.defaultTo !== undefined) {
                try {
                    // ensure that a boolean has the correct externalBoolean. Only true and false allowed.
                    if ((0, typeguards_1.isBooleanBaseFieldConfig)(baseField) &&
                        !['true', 'false'].includes(jf.defaultTo)) {
                        // eslint-disable-next-line no-throw-literal
                        throw {
                            name: baseField.field,
                            value: jf.defaultTo,
                            expectedType: baseField.type,
                            expectedFormat: getExpectedFormat(baseField),
                        };
                    }
                    (0, exports.getSanitizedDataTapeField)(baseField, jf.defaultTo, true);
                }
                catch (err) {
                    if ((0, typeguards_1.isInvalidField)(err)) {
                        errors.push({
                            type: 'metadataValidation',
                            message: `Must have valid defaultTo value for joinField - checkJoinTransformOp: ${jf.field} ${err.expectedType} ${jf.defaultTo}`,
                        });
                    }
                    else {
                        errors.push({
                            type: 'metadataValidation',
                            message: `Unexpected row sanitization error - checkJoinTransformOp: ${jf.field} ${err.message}`,
                        });
                    }
                }
            }
        });
    }
    return errors.length > 0 ? errors : null;
};
const getJoinTransformOpOutputDataTape = (transformation) => {
    const errors = checkJoinTransformOp(transformation);
    if (errors) {
        throw errors;
    }
    const joinBaseFieldsMap = lodash_1.default.keyBy(transformation.joinDataTape.baseFields, 'field');
    const baseFields = [
        ...transformation.srcDataTape.baseFields,
        ...lodash_1.default.map(transformation.joinFields, (jf) => joinBaseFieldsMap[jf.field]),
    ];
    return {
        baseFields,
        delimiter: ',',
        sanitizedFieldNames: false,
    };
};
exports.getJoinTransformOpOutputDataTape = getJoinTransformOpOutputDataTape;
const checkJoinTransformData = ({ transformation, joinData, }) => {
    const errors = [];
    // ensure joinOnField is unqiue in otherData so a single match with srcData is possible
    const dupeOtherData = lodash_1.default.chain(joinData)
        .groupBy(transformation.joinOnField)
        .pickBy((f) => f.length > 1)
        .keys()
        .value();
    if (dupeOtherData.length !== 0) {
        errors.push({
            type: 'metadataValidation',
            message: `Duplicate join data on join field - checkJoinTransformData: ${transformation.joinOnField}`,
        });
    }
    if (errors.length > 0) {
        throw errors;
    }
};
const joinTransformOp = (input) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const srcCsvString = common_1.finleyBuffer.toString(input.srcDataTapeBuffer);
        const srcDelimiter = input.transformation.srcDataTape.delimiter;
        const joinCsvString = common_1.finleyBuffer.toString(input.joinDataTapeBuffer);
        const joinDelimiter = input.transformation.joinDataTape.delimiter;
        // get the outputDataTape early in the process to error out if necessary
        const outputDataTape = (0, exports.getJoinTransformOpOutputDataTape)(input.transformation);
        const [srcHeaders, joinHeaders] = yield Promise.all([
            (0, exports.getHeaders)({
                csvString: srcCsvString,
                delimiter: srcDelimiter,
                sanitizeFieldNames: false, // field names are sanitized previously by sanitize op
            }),
            (0, exports.getHeaders)({
                csvString: joinCsvString,
                delimiter: joinDelimiter,
                sanitizeFieldNames: false, // field names are sanitized previously by sanitize op
            }),
        ]);
        const [srcData, joinData] = yield Promise.all([
            (0, csvtojson_1.default)({ headers: srcHeaders, delimiter: srcDelimiter }).fromString(srcCsvString),
            (0, csvtojson_1.default)({
                headers: joinHeaders,
                delimiter: joinDelimiter,
            }).fromString(joinCsvString),
        ]);
        const joinOnField = input.transformation.joinOnField;
        const errorOnMissingJoinRow = input.transformation.errorOnMissingJoinRow;
        // ensure the data is joinable
        checkJoinTransformData({
            transformation: input.transformation,
            joinData,
        });
        // set joinOnDefault once for reuse in map
        let joinOnDefault;
        if (!errorOnMissingJoinRow) {
            const defaultedJoinFields = lodash_1.default.filter(input.transformation.joinFields, (jf) => jf.defaultTo !== undefined);
            const joinBaseFieldsMap = lodash_1.default.keyBy(input.transformation.joinDataTape.baseFields, 'field');
            const joinFieldsMap = lodash_1.default.keyBy(defaultedJoinFields, 'field');
            joinOnDefault = lodash_1.default.reduce(joinFieldsMap, (acc, jf, prop) => (Object.assign(Object.assign({}, acc), { [prop]: (0, exports.getSanitizedDataTapeField)(joinBaseFieldsMap[prop], jf.defaultTo, true) })), {});
        }
        const srcFields = input.transformation.srcDataTape.baseFields.map((f) => f.field);
        const joinFields = lodash_1.default.map(input.transformation.joinFields, (jf) => jf.field);
        const joinDataErrors = [];
        const data = lodash_1.default.map(srcData, (src, index) => {
            // check if joinOn matches in src and join data
            let joinMatch = lodash_1.default.find(joinData, (jd) => src[joinOnField] === jd[joinOnField]);
            if (joinMatch === undefined && errorOnMissingJoinRow) {
                joinDataErrors.push({
                    type: 'joinValidation',
                    // excel/csv have a header row and are 1 based arrays, so adjust for our 0 based rowData
                    row: index + STANDARD_ROW_INDEX_OFFSET,
                    message: `Required joinOnField missing in join data tape: ${joinOnField} ${src[joinOnField]}`,
                });
                return {};
            }
            // if a match does not exist, if required with a default, set to that value
            if (joinMatch === undefined && joinOnDefault !== undefined) {
                joinMatch = joinOnDefault;
            }
            return Object.assign(Object.assign({}, lodash_1.default.pick(src, srcFields)), lodash_1.default.pick(joinMatch, joinFields));
        });
        // if any errors exist, entire run is invalid
        if (joinDataErrors.length !== 0) {
            return {
                isValid: false,
                errors: joinDataErrors,
            };
        }
        const outputBuffer = (0, exports.rowDataToCsv)(data, outputDataTape);
        return {
            isValid: true,
            data: { outputDataTape, outputBuffer },
            supplementals: [],
        };
    }
    catch (errors) {
        // if we don't have an array of error, then something went wrong, throw the error
        if (common_1.typeguards.isErrorType(errors) || !lodash_1.default.isArray(errors)) {
            throw errors;
        }
        return { isValid: false, errors };
    }
});
exports.joinTransformOp = joinTransformOp;
//# sourceMappingURL=helpers.js.map